import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Grid,
  Button,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Pagination,
  useTheme,
  useMediaQuery,
  Stack,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { PieChart } from "@mui/x-charts";
import {
  IContestDetail,
  IActivity,
  IContest,
  IContestResponse,
} from "../../../api/types";
import { useCustomBreadcrumb } from "@/contexts/CustomBreadcrumb";
import { useParams } from "react-router-dom";

interface ContestDetailProps {
  data: IContestDetail;
  onViewMissionDetail: (mission: any) => void;
  onBack: () => void;
  onReload: () => void;
  urlDigitalTwin?: string;
}

const ITEMS_PER_PAGE = 4;

const ContestDetail = ({
  data,
  onViewMissionDetail,
  onBack,
  onReload,
}: ContestDetailProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedSection, setSelectedSection] = useState<number | "all">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const contestId = window.location.pathname.split("/").pop();
  const contest = data?.contest?.data?.find(
    (c) => c.id.toString() === contestId
  );
  const customBreadcrumb = useCustomBreadcrumb();

  useEffect(() => {
    if (contest) {
      customBreadcrumb.addBreadcrumbRule({
        path: `${process.env.PREFIX}/my-contests/${contest.id}`,
        breadcrumb: contest.title,
      });
    }
  }, [contest]);
  // const contest = data.contest.data[0];
  const sections = data.section;
  const activities = data.activity;

  // Data for pie chart (from props)
  const pieChartData = [
    {
      id: 0,
      value: data.my_score.effort_score,
      color: "#1a73e8",
    },
    {
      id: 1,
      value: data.my_score.performance_score,
      color: "#34a853",
    },
    {
      id: 2,
      value: data.my_score.challenge_score,
      color: "#fbbc04",
    },
  ];

  // Fixed data for description
  const pieDescriptionData = [
    {
      id: 0,
      value: 30,
      label: "Effort Score",
      color: "#1a73e8",
      description: "Points for task completion and setup",
    },
    {
      id: 1,
      value: 50,
      label: "Performance Score",
      color: "#34a853",
      description: "Points for path completion and obstacle avoidance",
    },
    {
      id: 2,
      value: 20,
      label: "Challenge Score",
      color: "#fbbc04",
      description: "Additional points for advanced objectives",
    },
  ];

  // Filter activities based on selected section
  const filteredActivities: IActivity[] =
    selectedSection === "all"
      ? activities
      : activities.filter((activity) => activity.parentId === selectedSection);

  // Calculate pagination
  const totalPages = Math.ceil(filteredActivities.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedActivities = filteredActivities.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const generateActivityUrl = (activity: IActivity) => {
    const params = {
      pcontest: contest.id,
      activityid: activity.cmid,
      levelid: contest.level_id,
      orgid: data.orgid,
      activityname: encodeURIComponent(activity.activityName),
    };

    const queryString = Object.entries(params)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    return `${process.env.URL_DIGITAL_TWIN}?${queryString}`;
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2 } }}>
      {/* Header section */}
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <IconButton onClick={onBack} sx={{ mb: 2 }}>
          <ArrowBack />
        </IconButton>

        <Typography
          variant={isMobile ? "h5" : "h4"}
          color="primary"
          sx={{ mb: 2 }}
        >
          {contest ? `${contest.level || ""} Level - ${contest.title}` : ""}
        </Typography>

        <Typography
          color="textSecondary"
          sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
        >
          {contest ? contest.excerpt : ""}
        </Typography>
      </Box>

      {/* Charts and Description */}
      <Grid container spacing={2} sx={{ mb: { xs: 2, sm: 4 } }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Scoring Overview
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems="center"
            >
              <Box
                sx={{
                  width: { xs: "100%", sm: "60%" },
                  height: { xs: 200, sm: 300 },
                }}
              >
                <PieChart
                  series={[
                    {
                      data: pieChartData,
                      highlightScope: { faded: "global", highlighted: "item" },
                      cx: isMobile ? 100 : 150,
                      cy: isMobile ? 100 : 150,
                      innerRadius: isMobile ? 30 : 40,
                      outerRadius: isMobile ? 80 : 120,
                      arcLabel: () => '', // remove text labels
                    },
                  ]}
                  width={isMobile ? 200 : 300}
                  height={isMobile ? 200 : 300}
                  slotProps={{
                    legend: {
                      hidden: true,
                    },
                  }}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: "40%" } }}>
                {pieDescriptionData.map((item) => (
                  <Box key={item.id} sx={{ mb: 2 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: item.color,
                          mr: 1,
                        }}
                      />
                      <Typography variant="body1" fontWeight="bold">
                        {item.label}: {item.value}%
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ pl: 3 }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Competition Description
            </Typography>
            <div
              dangerouslySetInnerHTML={{
                __html: contest ? ` ${contest.description || ""}` : "",
              }}
            />
          </Card>
        </Grid>
      </Grid>

      {/* Skills to Master */}
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Skills to Master
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Tabs
            value={selectedSection}
            onChange={(_, value) => {
              setSelectedSection(value);
              setCurrentPage(1);
            }}
            sx={{
              mb: 2,
              ".MuiTabs-flexContainer": {
                flexWrap: "wrap",
                gap: 1,
              },
            }}
            variant={isMobile ? "scrollable" : "standard"}
            scrollButtons={isMobile ? "auto" : false}
          >
            <Tab
              label="All"
              value="all"
              sx={{
                minWidth: "auto",
                mr: 2,
                "&.Mui-selected": { color: "primary.main" },
              }}
            />
            {sections.map((section) => (
              <Tab
                key={section.sectionId}
                label={section.sectionName}
                value={section.sectionId}
                sx={{
                  minWidth: "auto",
                  mr: 2,
                  "&.Mui-selected": { color: "primary.main" },
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tasks and Progress */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Tasks and Progress</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={onReload}
            fullWidth={isMobile}
          >
            Reload mission results
          </Button>
        </Stack>

        <Grid container spacing={2}>
          {paginatedActivities.map((activity) => (
            <Grid item xs={12} key={activity.id}>
              <Card sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {activity.activityName}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 2 }}
                      dangerouslySetInnerHTML={{ __html: activity.desc }}
                    />
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Box
                        sx={{ display: "flex", gap: 2, alignItems: "center" }}
                      >
                        <Chip
                          label={activity.stateComplete}
                          color={
                            activity.stateComplete === "Completed"
                              ? "success"
                              : "warning"
                          }
                          size="small"
                        />
                        {activity.stateComplete === "Completed" && (
                          <Typography variant="body2" color="textSecondary">
                            (Min: {activity.min_score}, Max:{" "}
                            {activity.max_score})
                          </Typography>
                        )}
                      </Box>
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                      >
                        <Button
                          variant="outlined"
                          onClick={() => onViewMissionDetail(activity)}
                          fullWidth={isMobile}
                        >
                          Detail
                        </Button>
                        <Button
                          variant="contained"
                          href={generateActivityUrl(activity)}
                          target="_blank"
                          fullWidth={isMobile}
                        >
                          Start
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Typography variant="body2" color="textSecondary">
                      Last Updated: {activity.timeCompleted}
                    </Typography>
                  </Box>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ContestDetail;
